import { isRouteErrorResponse } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa'; // Ícone de alerta
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    // Log error to an error reporting service
    this.setState({ error, errorInfo });
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;

    if (hasError && error) {
      let title = 'Oops...!';
      let message = 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.';
      let stack: string | undefined;

      // Lógica para tratar erros de rota
      if (isRouteErrorResponse(error)) {
        title = error.status === 404 ? '404 - Página Não Encontrada' : 'Erro';
        message =
          error.status === 404
            ? 'A página que você está procurando não existe.'
            : error.statusText || message;
      } else if (error instanceof Error) {
        message = error.message;
        stack = error.stack;
      }

      return (
        <main className='flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white px-4 py-8'>
          <div className='text-center space-y-6 w-full max-w-lg'>
            {/* Ícone de alerta com gradiente e título */}
            <div className='flex justify-center items-center space-x-4'>
              <FaExclamationTriangle className='text-4xl sm:text-6xl text-yellow-400' />
              <h1 className='text-4xl sm:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-600'>
                {title}
              </h1>
            </div>
            {/* Mensagem de erro com uma cor mais suave */}
            <p className='text-base sm:text-lg text-gray-300 mx-auto font-medium'>{message}</p>

            {/* Exibe o stack apenas no ambiente de desenvolvimento */}
            {process.env.NODE_ENV === 'development' && stack && (
              <details className='mt-4 text-left max-w-full mx-auto bg-white p-4 rounded-md shadow-md text-gray-800'>
                <summary className='font-medium text-gray-600 cursor-pointer'>
                  Stack Trace (visível apenas no desenvolvimento)
                </summary>
                <pre className='mt-2 overflow-x-auto text-sm text-gray-700'>
                  <code>{stack}</code>
                </pre>
              </details>
            )}

            {/* Exibindo informações adicionais de erro apenas no ambiente de desenvolvimento */}
            {process.env.NODE_ENV === 'development' && errorInfo && (
              <details className='mt-4 text-left max-w-full mx-auto bg-white p-4 rounded-md shadow-md text-gray-800'>
                <summary className='font-medium text-gray-600 cursor-pointer'>
                  Informações de erro (visível apenas no desenvolvimento)
                </summary>
                <pre className='mt-2 overflow-x-auto text-sm text-gray-700'>
                  <code>{JSON.stringify(errorInfo, null, 2)}</code>
                </pre>
              </details>
            )}
          </div>
          <a
            href='/'
            className='mt-8 inline-block px-6 py-3 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition ease-in-out duration-300'
          >
            Voltar para a página inicial
          </a>
        </main>
      );
    }

    // Renderiza os filhos normalmente
    return this.props.children;
  }
}
